import { Card, EmptyState, Loading, Notice, PageHeader, PersonRow, Pill, Rows, SearchBox, sentenceCase } from '@trellistry/ui';
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { api } from '../api.js';

interface PersonSummary {
  id: string; displayName: string; nameCn: string | null; pinyin: string | null; knownAs: string | null; status: string; membershipType: string | null;
  matched?: { kind: string; value: string };
}

const matchWords: Record<string, string> = { name: 'name', name_cn: 'Chinese name', pinyin: 'pinyin', known_as: 'known as', phone: 'phone', email: 'email', postal: 'postal code', street: 'address' };

/** The directory: a list, or a search across every field without naming it (PLAT-15), the match labelled. */
export function People() {
  // the box keeps its own text, so fast typing loses nothing; the term reaches the address (?q=) after a pause,
  // so a search can be shared and survives a reload
  const [params, setParams] = useSearchParams();
  const [text, setText] = useState(params.get('q') ?? '');
  const q = text.trim();
  const [people, setPeople] = useState<PersonSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(() => {
      setParams(q ? { q } : {}, { replace: true });
      api<{ people: PersonSummary[] }>(`/people?q=${encodeURIComponent(q)}`)
        .then((r) => { setPeople(r.people); setError(null); })
        .catch(() => setError('We couldn’t load the directory just now.'));
    }, q ? 200 : 0);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <div className="reading-width">
      <PageHeader title="People" meta="Find someone by name, Chinese name, pinyin, phone or postal code." />
      <div className="person-find">
        <SearchBox label="Find someone" value={text} onChange={(e) => setText(e.target.value)} placeholder="Start typing…" autoFocus />
      </div>
      {error && <Notice tone="error">{error}</Notice>}
      <Card>
        {people === null ? (!error && <Loading>Loading…</Loading>) : people.length === 0 ? (
          <EmptyState title={q ? `Nobody matches “${q}”` : 'Nobody here yet'}>
            <p>{q ? 'Check the spelling, or try a phone number or postal code.' : 'People appear here once they are imported or added.'}</p>
          </EmptyState>
        ) : (
          <Rows items={people} keyOf={(p) => p.id} render={(p) => (
            <PersonRow
              as={Link}
              to={`/people/${p.id}`}
              name={p.displayName}
              nameCn={p.nameCn}
              pinyin={p.pinyin}
              knownAs={p.knownAs}
              sub={p.membershipType && sentenceCase(p.membershipType)}
              end={p.matched && <Pill>Matched {matchWords[p.matched.kind] ?? p.matched.kind}</Pill>}
            />
          )} />
        )}
      </Card>
    </div>
  );
}
